/**
 * Utility functions for validating and sanitizing template and folder names
 */

/**
 * Regex pattern for validating folder names
 * Allows only letters (A-Z, a-z), numbers (0-9), underscores (_), hyphens (-), and spaces
 * Folder names must start and end with a letter or number
 */
export const FOLDER_NAME_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9](?:[A-Za-z0-9_-]*[A-Za-z0-9])?(?: [A-Za-z0-9](?:[A-Za-z0-9_-]*[A-Za-z0-9])?)*$/;
export const FOLDER_NAME_MIN_LENGTH = 3;
export const FOLDER_NAME_MAX_LENGTH = 20;

/**
 * Template name validation constraints
 */
export const TEMPLATE_NAME_MIN_LENGTH = 3;
export const TEMPLATE_NAME_MAX_LENGTH = 32;
// Allows single-space-delimited words and " - " separators (e.g., "Reminder Email - 14 Day").
export const TEMPLATE_NAME_REGEX = /^(?=.*[A-Za-z])[A-Za-z0-9](?:[A-Za-z0-9_~-]*[A-Za-z0-9])?(?:(?: [A-Za-z0-9](?:[A-Za-z0-9_~-]*[A-Za-z0-9])?)|(?: - [A-Za-z0-9](?:[A-Za-z0-9_~-]*[A-Za-z0-9])?))*$/;

/**
 * Sanitizes a template name by removing invalid characters
 * Allows letters, numbers, spaces, underscores, and hyphens
 * @param s - The string to sanitize
 * @returns The sanitized string
 */
export const sanitizeTemplateName = (s: string): string => {
  return s.replace(/[^A-Za-z0-9 _~-]/g, "");
};

/**
 * Validates if a template name matches the required format
 * @param templateName - The template name to validate
 * @returns True if the template name is valid, false otherwise
 */
export const isValidTemplateName = (templateName: string): boolean => {
  // Error message: Name format is invalid. It must start and end with a letter or number, with no leading, trailing, or consecutive spaces. Also, all-numeric names are not allowed.
  return (
    templateName.trim().length >= TEMPLATE_NAME_MIN_LENGTH &&
    templateName.length <= TEMPLATE_NAME_MAX_LENGTH &&
    TEMPLATE_NAME_REGEX.test(templateName)
  );
};

/**
 * Sanitizes a folder name by removing invalid characters
 * Allows only letters (A-Z, a-z), numbers (0-9), underscores (_), hyphens (-), and spaces
 * @param s - The string to sanitize
 * @returns The sanitized string
 */
export const sanitizeFolderName = (s: string): string => {
  return s.replace(/[^A-Za-z0-9 _-]/g, "");
};

/**
 * Validates if a folder name matches the required format
 * @param folderName - The folder name to validate
 * @returns True if the folder name is valid, false otherwise
 */
export const isValidFolderName = (folderName: string): boolean => {
  return (
    folderName.length >= FOLDER_NAME_MIN_LENGTH &&
    folderName.length <= FOLDER_NAME_MAX_LENGTH &&
    FOLDER_NAME_REGEX.test(folderName)
  );
};

/**
 * Normalizes a string for comparison (lowercase and trimmed)
 * @param s - The string to normalize
 * @returns The normalized string
 */
export const normalizeForComparison = (s: string): string => {
  return s.trim().toLowerCase();
};
